// Pixar Talking Ad's draft as the form keeps it (spec G14-pixar-talking-ad, G14 PR 18a): the
// server holds the member's draft (`.../templates/pixar-talking-ad/draft`), so it survives the
// panel; the form reads it, keeps its edits there, and asks the server's pack reader, directors
// and stills (live's `usePixarTalkingDraft`, which ran them from the browser). As Street
// Interview Ad's (`street-draft.ts`), with one approval before the video.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetPixarTalkingAdDraftQueryKey,
  useApprovePixarTalkingAdScript,
  useGetPixarTalkingAdDraft,
  useRegeneratePixarTalkingAdStill,
  useResetPixarTalkingAdDraft,
  useWritePixarTalkingAdDraft,
  useWritePixarTalkingAdScript,
  writePixarTalkingAdDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  OutputRef,
  PixarDraftRun,
  PixarDraftStill,
  PixarDraftStillName,
  PixarRunClip,
  PixarScript,
  PixarStills,
  PixarTakes,
  PixarTalkingAdDraft,
} from "../../../api/generated/model";
import {
  PixarTalkingAdApproval,
  PixarTalkingAdDraftWrite,
  PixarTalkingAdInputs,
  PixarTalkingAdScriptAsk,
  type PixarTalkingAdDraftWriteOutput,
  type PixarTalkingAdInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface PixarForm {
  inputs: PixarTalkingAdInputsOutput;
  script: PixarScript | null;
}

/** What each still is drawn from (live's `STILL_GRAPH.sources`): the character chain, each from
 * the one before; the sheet and the end card from no still. */
const STILL_SOURCES: Readonly<Partial<Record<PixarDraftStillName, PixarDraftStillName>>> = {
  opening: "char_sheet",
  hold: "opening",
  closing: "hold",
};

/** The video's settings live's footer keeps open at Cast & set (the subtitles and the
 * resolution): the form's own until "Generate final video" sends them. The end card is fixed at
 * Approve script, which draws its still or not. */
export interface VideoSettings {
  subtitles: boolean;
  caption_position: PixarTalkingAdInputsOutput["caption_position"];
  caption_style_id: string;
  resolution: PixarTalkingAdInputsOutput["resolution"];
}

/** Live's words for a clip the video model refused (`core/constants.ts`), shown on its frames. */
export const FRAME_REFUSED =
  "The video model refused the clip this frame belongs to — regenerate this frame or the other one of the clip.";
export const END_CARD_REFUSED = "The video model refused the end card — regenerate it, or build without it.";
export const BUILD_WITHOUT_END_CARD = "Build without end card";

/** The frames each clip is drawn between (live's `video-chain.ts`): `k1` from the opening to the
 * hold, `k2` from the hold to the closing, `k3` from the end card. */
const CLIP_FRAMES: Readonly<Record<PixarRunClip, readonly (keyof PixarStills)[]>> = {
  k1: ["opening", "hold"],
  k2: ["hold", "closing"],
  k3: ["end_card"],
};

function sameRef(a: OutputRef | null | undefined, b: OutputRef | null | undefined): boolean {
  return a !== null && a !== undefined && b !== null && b !== undefined && a.job_id === b.job_id && a.output_index === b.output_index;
}

function hasEndCard(stills: PixarStills): boolean {
  return stills.end_card !== undefined && stills.end_card !== null;
}

/** Whether a clip of the last run was drawn between the frames the draft holds now. */
function framesUnchanged(run: PixarDraftRun, clip: PixarRunClip, stills: PixarStills): boolean {
  return CLIP_FRAMES[clip].every((frame) => sameRef(run.stills[frame], stills[frame]));
}

/**
 * Live's `isTakeCurrent`: the clips of the last run, once it has ended, that were delivered
 * between the frames the next run is drawn between, at its resolution; the next run keeps them
 * as they are (`PixarTakes`, Engine-8) and renders the rest. The end card's only where the next
 * run has one.
 */
export function currentTakes(
  run: PixarDraftRun | null,
  stills: PixarStills,
  resolution: VideoSettings["resolution"],
): PixarTakes {
  const takes: PixarTakes = {};
  if (run === null || !run.ended || run.resolution !== resolution) return takes;
  for (const take of run.clips) {
    if (take.output === null || !framesUnchanged(run, take.clip, stills)) continue;
    if (take.clip === "k3" && !hasEndCard(stills)) continue;
    takes[take.clip] = take.output;
  }
  return takes;
}

/**
 * The frames to flag (live's refused-take notice on each still card): those of a clip the last
 * run's video model refused, while they are still the frames it was drawn between; a frame
 * regenerated since is a new take, so its flag goes.
 */
export function refusedFrames(
  run: PixarDraftRun | null,
  stills: PixarStills,
): Readonly<Partial<Record<PixarDraftStillName, string>>> {
  const flags: Partial<Record<PixarDraftStillName, string>> = {};
  if (run === null || !run.ended) return flags;
  for (const take of run.clips) {
    if (!take.refused || !framesUnchanged(run, take.clip, stills)) continue;
    for (const frame of CLIP_FRAMES[take.clip]) flags[frame] = take.clip === "k3" ? END_CARD_REFUSED : FRAME_REFUSED;
  }
  return flags;
}

/** The stills the draft drew, by name, once every one it draws is done; null before. */
export function drawnStills(draft: PixarTalkingAdDraft): PixarStills | null {
  if (draft.stage !== "cast") return null;
  const drawn = Object.fromEntries(
    draft.stills.flatMap((each) => (each.output ? [[each.name, each.output] as const] : [])),
  );
  if (draft.stills.length === 0 || draft.stills.some((each) => !(each.name in drawn))) return null;
  const { opening, hold, closing, end_card: endCard } = drawn;
  if (opening === undefined || hold === undefined || closing === undefined) return null;
  return { opening, hold, closing, ...(endCard === undefined ? {} : { end_card: endCard }) };
}

/** "Generate final video"'s body (live's hand-off): the approved script and spec, the stills the
 * clips are drawn between by reference, the end card as approved (or left out: live's "Build
 * without end card"), the footer's settings, and the last run's clips the run keeps as they
 * are (`currentTakes`); null until every still the draft draws is done. */
export function videoBody(draft: PixarTalkingAdDraft, settings: VideoSettings, withoutEndCard = false) {
  if (draft.script === null || draft.spec === null) return null;
  const drawn = drawnStills(draft);
  if (drawn === null) return null;
  const { end_card: endCard, ...frames } = drawn;
  const stills: PixarStills = endCard === undefined || endCard === null || withoutEndCard ? frames : { ...frames, end_card: endCard };
  const takes = currentTakes(draft.run, stills, settings.resolution);
  return {
    type: "video.template" as const,
    inputs: {
      template: "pixar-talking-ad" as const,
      script: draft.script,
      spec: draft.spec,
      stills,
      end_card: hasEndCard(stills),
      ...settings,
      ...(Object.keys(takes).length === 0 ? {} : { takes }),
    },
  };
}

/** The draft refetched while it works: every 2 s while a director or a still works, and a moment
 * after (a step's end moves the draft a moment after its job ends); every 10 s while its last
 * run runs, for its takes and its flags once it ends (a run takes minutes). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
const RUN_POLL_MS = 10_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** A still under way (live's `isStillWaiting`): drawing, or pending on a still that is done or
 * still coming. */
export function stillWaiting(name: PixarDraftStillName, stills: readonly PixarDraftStill[]): boolean {
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  const source = STILL_SOURCES[name];
  if (source === undefined) return false;
  return stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, stills);
}

/** Where the draft's work stands, as live's progress line names it. */
export interface DraftWork {
  reading: boolean;
  writing: boolean;
  planning: boolean;
  drawing: boolean;
}

export function workOf(draft: PixarTalkingAdDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  return {
    reading: draft.pack_director === "working",
    writing: draft.script_director === "working" || (answering && draft.pack_director !== "working"),
    planning: draft.shot_director === "working" || (draft.stage === "cast" && draft.stills.length === 0),
    drawing: draft.stills.some((each) => stillWaiting(each.name, draft.stills)),
  };
}

function working(work: DraftWork): boolean {
  return work.reading || work.writing || work.planning || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: PixarTalkingAdDraft): PixarForm {
  const inputs = PixarTalkingAdInputs.safeParse(draft.inputs);
  return {
    inputs: inputs.success ? inputs.data : PixarTalkingAdInputs.parse({}),
    script: draft.script,
  };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses; each ask
 * answers with the draft, shown from then on.
 */
export function usePixarDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetPixarTalkingAdDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<PixarForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetPixarTalkingAdDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        let poll: number | false = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        if (poll === false && data?.run !== undefined && data.run !== null && !data.run.ended) poll = RUN_POLL_MS;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: PixarTalkingAdDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWritePixarTalkingAdScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApprovePixarTalkingAdScript(asks);
  const regeneration = useRegeneratePixarTalkingAdStill(asks);
  const reset = useResetPixarTalkingAdDraft(asks);
  const asking = script.isPending || approval.isPending || regeneration.isPending || reset.isPending;
  const work: DraftWork =
    draft === undefined
      ? { reading: false, writing: false, planning: false, drawing: false }
      : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWritePixarTalkingAdDraft({
    mutation: {
      onSuccess: () => setFailure(null),
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: PixarTalkingAdDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writePixarTalkingAdDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = PixarTalkingAdDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    /** The draft read again now: after "Generate final video", for the run it keeps. */
    refresh: refetch,
    form,
    work,
    busy,
    editable,
    failure,
    approving: approval.isPending,
    resetting: reset.isPending,
    edit(change: Partial<PixarForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    editInputs(change: Partial<PixarTalkingAdInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = PixarTalkingAdScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = PixarTalkingAdApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    regenerate(name: PixarDraftStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
